// Iter v34z (#3 Phase C) — Master Batching tabbed UI (Mix Design composition, Sopir, Truk).
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Save, X, Layers } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, PageHeader, Button, Field, Input, Modal, Select, Textarea } from "@/components/ui-kit";
import { formatNumber, parseQty } from "@/lib/format";

const TABS = [
  { key: "mix", label: "Mix Design (Mutu Beton)" },
  { key: "driver", label: "Sopir" },
  { key: "truck", label: "Truk" },
  { key: "sender", label: "Pengirim" },
  { key: "receiver", label: "Penerima" },
  // Iter v34bl (Task #3) — Master Lokasi Proyek untuk dropdown SJ Ready Mix.
  { key: "location", label: "Lokasi Proyek" },
  // Iter v34br (Task 8/9) — Master untuk Solar tracking distribusi.
  { key: "alat", label: "Alat Berat" },
  { key: "genset", label: "Genset" },
];

export default function MasterBatching() {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("batching.sj.create");
  // Iter v34bm — Master Lokasi Proyek pakai perm khusus (super admin default).
  const canEditLocation = hasPerm("batching.locations.manage");
  const [tab, setTab] = useState("mix");

  return (
    <div className="max-w-[1400px] mx-auto p-4 md:p-6 lg:p-8 space-y-6">
      <PageHeader
        overline="Batching Plant · Master Data"
        title="Master Batching"
        description="Kelola Mix Design (komposisi material per m³), Sopir, dan Truk Mixer."
        icon={Layers}
      />
      <div className="flex gap-1 border-b border-stone-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm border-b-2 transition-colors ${
              tab === t.key
                ? "border-terra-600 text-terra-700 font-semibold"
                : "border-transparent text-stone-500 hover:text-stone-800"
            }`}
            data-testid={`master-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "mix" && <MixDesignTab canEdit={canEdit} />}
      {tab === "driver" && <DriverTab canEdit={canEdit} />}
      {tab === "truck" && <TruckTab canEdit={canEdit} />}
      {tab === "sender" && <SimpleNameTab canEdit={canEdit} endpoint="senders" label="Pengirim" />}
      {tab === "receiver" && <SimpleNameTab canEdit={canEdit} endpoint="receivers" label="Penerima" />}
      {tab === "location" && <SimpleNameTab canEdit={canEditLocation} endpoint="locations" label="Lokasi Proyek" />}
      {/* Iter v34br (Task 8/9) — Master alat/mobil/genset untuk Solar distribusi. */}
      {tab === "alat" && <SimpleNameTab canEdit={canEdit} endpoint="alat" label="Alat Berat" />}
      {tab === "genset" && <SimpleNameTab canEdit={canEdit} endpoint="genset" label="Genset" />}
    </div>
  );
}

// ============================================================================
// Iter v34bd — Simple master (Pengirim / Penerima) with name + note
// ============================================================================
function SimpleNameTab({ canEdit, endpoint, label }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", note: "" });
  // Iter v34ca (#5) — Inline edit per row
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", note: "" });
  const load = () => api.get(`/batching/${endpoint}`).then((r) => setRows(r.data || []));
  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    if (!form.name.trim()) return;
    await api.post(`/batching/${endpoint}`, form);
    setForm({ name: "", note: "" }); load();
    toast.success(`${label} ditambahkan`);
  };
  const del = async (id) => {
    if (!window.confirm(`Hapus ${label.toLowerCase()}?`)) return;
    await api.delete(`/batching/${endpoint}/${id}`); load();
  };
  const startEdit = (r) => { setEditId(r.id); setEditForm({ name: r.name || "", note: r.note || "" }); };
  const cancelEdit = () => { setEditId(null); setEditForm({ name: "", note: "" }); };
  const saveEdit = async (id) => {
    if (!editForm.name.trim()) return toast.error("Nama wajib diisi");
    try {
      await api.patch(`/batching/${endpoint}/${id}`, editForm);
      toast.success("Berhasil disimpan");
      cancelEdit(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
  };
  return (
    <Card>
      {canEdit && (
        <div className="flex gap-2 p-4 border-b border-stone-200">
          <Input placeholder={`Nama ${label.toLowerCase()}`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid={`${endpoint}-name-input`} />
          <Input placeholder="Jabatan / Catatan (opsional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          <Button onClick={submit} data-testid={`${endpoint}-add-btn`}><Plus className="h-4 w-4" /> Tambah</Button>
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-stone-50 border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
            <th className="py-3 px-3">Nama</th>
            <th className="py-3 px-3">Catatan / Jabatan</th>
            <th className="py-3 px-3 text-right">Aksi</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={3} className="py-8 text-center text-stone-400 text-xs">Belum ada {label.toLowerCase()}</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-stone-100">
              {editId === r.id ? (
                <>
                  <td className="py-1 px-2"><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid={`${endpoint}-edit-name-${r.id}`} /></td>
                  <td className="py-1 px-2"><Input value={editForm.note} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} /></td>
                  <td className="py-1 px-2 text-right whitespace-nowrap">
                    <button onClick={() => saveEdit(r.id)} className="text-emerald-700 hover:text-emerald-900 px-2 text-xs font-semibold" data-testid={`${endpoint}-save-${r.id}`}>Simpan</button>
                    <button onClick={cancelEdit} className="text-stone-500 hover:text-stone-900 px-2 text-xs">Batal</button>
                  </td>
                </>
              ) : (
                <>
                  <td className="py-2.5 px-3 font-medium">{r.name}</td>
                  <td className="py-2.5 px-3 text-stone-500">{r.note || "—"}</td>
                  <td className="py-2.5 px-3 text-right whitespace-nowrap">
                    {canEdit && <button onClick={() => startEdit(r)} className="text-stone-500 hover:text-terra-700 px-2" data-testid={`${endpoint}-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>}
                    {canEdit && <button onClick={() => del(r.id)} className="text-stone-500 hover:text-red-700 px-2" data-testid={`${endpoint}-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

// ============================================================================
// Mix Design tab — komposisi material per m³
// ============================================================================

const blankMix = { name: "", note: "", composition: [] };

function MixDesignTab({ canEdit }) {
  const [mixes, setMixes] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(blankMix);

  const load = () => api.get("/batching/mix-designs").then((r) => setMixes(r.data || []));
  useEffect(() => {
    load();
    api.get("/batching/materials").then((r) => setMaterials(r.data || [])).catch(() => {});
  }, []);

  const openNew = () => { setEditId(null); setForm({ ...blankMix, composition: [] }); setOpen(true); };
  const openEdit = (m) => {
    setEditId(m.id);
    setForm({
      name: m.name || "",
      note: m.note || "",
      composition: (m.composition || []).map((c) => ({ ...c })),
    });
    setOpen(true);
  };

  const addComp = () => setForm({ ...form, composition: [...form.composition, { material_id: "", material_name: "", kg_per_m3: 0 }] });
  const rmComp = (i) => setForm({ ...form, composition: form.composition.filter((_, j) => j !== i) });
  const setComp = (i, key, val) => {
    const comp = [...form.composition];
    comp[i] = { ...comp[i], [key]: val };
    if (key === "material_id") {
      const mat = materials.find((m) => m.id === val);
      if (mat) comp[i].material_name = mat.name;
    }
    setForm({ ...form, composition: comp });
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nama Mix Design wajib diisi"); return; }
    const payload = {
      name: form.name.trim(),
      note: form.note || "",
      composition: form.composition
        .filter((c) => c.material_id && parseQty(c.kg_per_m3) > 0)
        .map((c) => ({
          material_id: c.material_id,
          material_name: c.material_name || "",
          kg_per_m3: parseQty(c.kg_per_m3),
        })),
    };
    try {
      if (editId) await api.patch(`/batching/mix-designs/${editId}`, payload);
      else await api.post("/batching/mix-designs", payload);
      toast.success(editId ? "Mix Design diperbarui" : "Mix Design dibuat");
      setOpen(false); setEditId(null); setForm(blankMix); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Hapus Mix Design "${name}"?`)) return;
    await api.delete(`/batching/mix-designs/${id}`);
    toast.success("Dihapus"); load();
  };

  return (
    <Card className="overflow-x-auto">
      <div className="flex items-center justify-between p-4 border-b border-stone-200">
        <div className="text-xs uppercase tracking-wider font-semibold text-stone-600">
          {mixes.length} Mix Design{mixes.length !== 1 ? "s" : ""}
        </div>
        {canEdit && (
          <Button onClick={openNew} data-testid="add-mix-design-btn">
            <Plus className="h-4 w-4" /> Mix Design Baru
          </Button>
        )}
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-stone-50 border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
            <th className="py-3 px-3">Nama (Mutu Beton)</th>
            <th className="py-3 px-3">Komposisi (kg/m³)</th>
            <th className="py-3 px-3">Catatan</th>
            <th className="py-3 px-3 text-right">Aksi</th>
          </tr>
        </thead>
        <tbody>
          {mixes.length === 0 && (
            <tr><td colSpan={4} className="py-12 text-center text-stone-400 text-xs">
              Belum ada Mix Design. Klik &quot;Mix Design Baru&quot; untuk membuat.
            </td></tr>
          )}
          {mixes.map((m, idx) => (
            <tr key={m.id} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`mix-row-${idx}`}>
              <td className="py-2.5 px-3 font-medium">{m.name}</td>
              <td className="py-2.5 px-3 text-stone-600">
                {(m.composition || []).length === 0 ? (
                  <span className="text-amber-600 text-xs">⚠ Belum ada komposisi (auto-deduct tidak aktif)</span>
                ) : (
                  <span className="text-xs">
                    {m.composition.map((c) => `${c.material_name || "?"}: ${formatNumber(c.kg_per_m3)}`).join(" · ")}
                  </span>
                )}
              </td>
              <td className="py-2.5 px-3 text-stone-500 text-xs">{m.note || "—"}</td>
              <td className="py-2.5 px-3 text-right">
                {canEdit && <button onClick={() => openEdit(m)} className="text-stone-500 hover:text-terra-700 px-2" data-testid={`mix-edit-${idx}`}><Pencil className="h-3.5 w-3.5" /></button>}
                {canEdit && <button onClick={() => del(m.id, m.name)} className="text-stone-500 hover:text-red-700 px-2" data-testid={`mix-delete-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Mix Design" : "Mix Design Baru"} size="lg">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Nama Mutu Beton" required>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="K-300 Non-Fly Ash" data-testid="mix-name" />
            </Field>
            <Field label="Catatan">
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="opsional" />
            </Field>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider font-semibold text-blue-600">Komposisi Material (per m³)</div>
              <Button variant="secondary" type="button" onClick={addComp} data-testid="mix-add-comp"><Plus className="h-3.5 w-3.5" /> Tambah Material</Button>
            </div>
            <div className="border border-stone-200 rounded">
              <div className="grid gap-2 p-2 bg-stone-50 text-[10px] uppercase tracking-wider font-semibold text-stone-600 border-b border-stone-200" style={{ gridTemplateColumns: "3fr 1.5fr 0.4fr" }}>
                <div>Material</div>
                <div className="text-right">kg per m³</div>
                <div></div>
              </div>
              {form.composition.length === 0 && (
                <div className="p-4 text-center text-stone-400 text-xs">Belum ada komposisi. Tanpa komposisi, auto-deduct stok dari SJ TIDAK akan jalan untuk mutu ini.</div>
              )}
              {form.composition.map((c, i) => (
                <div key={i} className="grid gap-2 p-2 border-b border-stone-100 items-center" style={{ gridTemplateColumns: "3fr 1.5fr 0.4fr" }}>
                  <Select value={c.material_id} onChange={(e) => setComp(i, "material_id", e.target.value)} data-testid={`mix-comp-material-${i}`}>
                    <option value="">— Pilih Material —</option>
                    {materials.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </Select>
                  <Input className="text-right font-mono-num" value={c.kg_per_m3} onChange={(e) => setComp(i, "kg_per_m3", e.target.value)} placeholder="380" data-testid={`mix-comp-kg-${i}`} />
                  <button onClick={() => rmComp(i)} className="text-stone-400 hover:text-red-600 justify-self-center" data-testid={`mix-comp-remove-${i}`}><X className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            {materials.length === 0 && (
              <p className="text-xs text-amber-600 mt-2">Belum ada master Material — buat dulu di halaman <strong>Stok Material Batching</strong>.</p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-stone-200">
            <Button variant="secondary" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={submit} data-testid="mix-submit"><Save className="h-4 w-4" /> {editId ? "Update" : "Simpan"}</Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}

// ============================================================================
// Driver tab — simple master
// ============================================================================

function DriverTab({ canEdit }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", note: "" });
  // Iter v34bd/be — Import driver dari master Karyawan (multi-select).
  const [candidates, setCandidates] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});   // { [empId]: true }
  const [candFilter, setCandFilter] = useState("");
  const load = () => api.get("/batching/drivers").then((r) => setRows(r.data || []));
  const loadCandidates = () => api.get("/batching/drivers/employee-candidates").then((r) => setCandidates(r.data || []));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!form.name.trim()) return;
    await api.post("/batching/drivers", form);
    setForm({ name: "", note: "" }); load();
    toast.success("Sopir ditambahkan");
  };
  const del = async (id) => {
    if (!window.confirm("Hapus sopir?")) return;
    await api.delete(`/batching/drivers/${id}`); load();
  };
  const openImport = async () => {
    await loadCandidates();
    setSelectedIds({});
    setCandFilter("");
    setImportOpen(true);
  };
  const toggleAll = () => {
    const filtered = candidates.filter((c) => !candFilter || `${c.name} ${c.position} ${c.department}`.toLowerCase().includes(candFilter.toLowerCase()));
    const allSelected = filtered.length > 0 && filtered.every((c) => selectedIds[c.id]);
    if (allSelected) {
      const next = { ...selectedIds };
      filtered.forEach((c) => delete next[c.id]);
      setSelectedIds(next);
    } else {
      const next = { ...selectedIds };
      filtered.forEach((c) => { next[c.id] = true; });
      setSelectedIds(next);
    }
  };
  const doBatchImport = async () => {
    const ids = Object.keys(selectedIds).filter((k) => selectedIds[k]);
    if (ids.length === 0) return toast.error("Pilih minimal 1 karyawan");
    try {
      const r = await api.post("/batching/drivers/import-employees-batch", { employee_ids: ids });
      const created = r.data?.created || 0;
      const skipped = r.data?.skipped || 0;
      toast.success(`${created} sopir ditambahkan${skipped > 0 ? ` · ${skipped} sudah ada, di-skip` : ""}`);
      setImportOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal import karyawan");
    }
  };
  const filteredCandidates = candidates.filter((c) => !candFilter || `${c.name} ${c.position} ${c.department}`.toLowerCase().includes(candFilter.toLowerCase()));
  const selectedCount = Object.values(selectedIds).filter(Boolean).length;
  return (
    <Card>
      {canEdit && (
        <div className="flex flex-wrap gap-2 p-4 border-b border-stone-200">
          <Input placeholder="Nama sopir" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="driver-name-input" />
          <Input placeholder="Catatan (opsional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          <Button onClick={submit} data-testid="driver-add-btn"><Plus className="h-4 w-4" /> Tambah Manual</Button>
          <Button variant="secondary" onClick={openImport} data-testid="driver-import-employee-btn">
            <Plus className="h-4 w-4" /> Import dari Karyawan
          </Button>
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-stone-50 border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
            <th className="py-3 px-3">Nama</th>
            <th className="py-3 px-3">Catatan / Sumber</th>
            <th className="py-3 px-3 text-right">Aksi</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={3} className="py-8 text-center text-stone-400 text-xs">Belum ada sopir</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-stone-100">
              <td className="py-2.5 px-3 font-medium">
                {r.name}
                {r.source === "employee_import" && <span className="ml-2 text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-sm">KARYAWAN</span>}
              </td>
              <td className="py-2.5 px-3 text-stone-500">{r.note || "—"}</td>
              <td className="py-2.5 px-3 text-right">
                {canEdit && <button onClick={() => del(r.id)} className="text-stone-500 hover:text-red-700 px-2"><Trash2 className="h-3.5 w-3.5" /></button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Modal open={importOpen} onClose={() => setImportOpen(false)} title="Import Sopir dari Karyawan" size="lg">
        <div className="space-y-3 p-1">
          <p className="text-xs text-stone-600">
            Pilih beberapa karyawan aktif untuk didaftarkan sebagai sopir batching plant sekaligus. Karyawan yang sudah ter-import tidak muncul di daftar.
          </p>
          <div className="flex gap-2 items-center">
            <Input placeholder="Cari nama / jabatan / departemen…" value={candFilter} onChange={(e) => setCandFilter(e.target.value)} data-testid="driver-import-filter" />
            <Button variant="ghost" onClick={toggleAll} data-testid="driver-import-toggle-all">
              {filteredCandidates.length > 0 && filteredCandidates.every((c) => selectedIds[c.id]) ? "Batal Pilih Semua" : "Pilih Semua"}
            </Button>
          </div>
          <div className="max-h-80 overflow-y-auto border border-stone-200 divide-y divide-stone-100">
            {filteredCandidates.length === 0 && (
              <div className="p-6 text-center text-xs text-amber-700 bg-amber-50">
                {candidates.length === 0 ? "Tidak ada karyawan yang bisa di-import." : "Tidak ada karyawan cocok dengan filter."}
              </div>
            )}
            {filteredCandidates.map((c) => (
              <label key={c.id} className="flex items-center gap-3 px-3 py-2 hover:bg-stone-50 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={!!selectedIds[c.id]}
                  onChange={(e) => setSelectedIds({ ...selectedIds, [c.id]: e.target.checked })}
                  data-testid={`driver-import-checkbox-${c.id}`}
                />
                <div className="flex-1">
                  <div className="font-medium">{c.name}</div>
                  <div className="text-[10px] text-stone-500">
                    {c.position || "-"}{c.department ? ` · ${c.department}` : ""}
                  </div>
                </div>
              </label>
            ))}
          </div>
          <div className="flex justify-between items-center pt-3 border-t border-stone-100">
            <div className="text-xs font-medium text-stone-700">
              {selectedCount} karyawan dipilih
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setImportOpen(false)}>Batal</Button>
              <Button onClick={doBatchImport} disabled={selectedCount === 0} data-testid="driver-import-confirm-btn">
                Import {selectedCount > 0 ? `${selectedCount} Karyawan` : ""}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </Card>
  );
}

// ============================================================================
// Truck tab — plate + capacity
// ============================================================================

function TruckTab({ canEdit }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ plate: "", capacity_m3: 7, panjang_m: 0, lebar_m: 0, tinggi_m: 0, truck_type: "readymix", note: "" });
  const load = () => api.get("/batching/trucks").then((r) => setRows(r.data || []));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!form.plate.trim()) return;
    await api.post("/batching/trucks", {
      ...form,
      capacity_m3: parseQty(form.capacity_m3) || 7,
      panjang_m: parseQty(form.panjang_m) || 0,
      lebar_m: parseQty(form.lebar_m) || 0,
      tinggi_m: parseQty(form.tinggi_m) || 0,
    });
    setForm({ plate: "", capacity_m3: 7, panjang_m: 0, lebar_m: 0, tinggi_m: 0, truck_type: "readymix", note: "" }); load();
    toast.success("Truk ditambahkan");
  };
  const del = async (id) => {
    if (!window.confirm("Hapus truk?")) return;
    await api.delete(`/batching/trucks/${id}`); load();
  };
  const typeLabel = (t) => t === "material" ? "Material (Dump)" : t === "both" ? "Ready Mix + Material" : "Ready Mix";
  const typePill = (t) => {
    const colors = { readymix: "bg-blue-100 text-blue-800", material: "bg-amber-100 text-amber-800", both: "bg-emerald-100 text-emerald-800" };
    return <span className={`text-[10px] px-1.5 py-0.5 rounded ${colors[t] || colors.readymix}`}>{typeLabel(t)}</span>;
  };
  return (
    <Card>
      {canEdit && (
        <div className="p-4 border-b border-stone-200 space-y-2">
          <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
            <Input className="col-span-2" placeholder="Plat (B 9988 XZ)" value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} data-testid="truck-plate-input" />
            <select
              value={form.truck_type}
              onChange={(e) => setForm({ ...form, truck_type: e.target.value })}
              className="border border-stone-300 bg-white rounded px-2 text-sm"
              data-testid="truck-type-input"
            >
              <option value="readymix">Ready Mix (Mixer)</option>
              <option value="material">Material (Dump)</option>
              <option value="both">Keduanya</option>
            </select>
            <Input type="number" step="0.5" placeholder="Kapasitas m³" value={form.capacity_m3} onChange={(e) => setForm({ ...form, capacity_m3: e.target.value })} />
            <Input type="number" step="0.1" placeholder="Panjang bak" value={form.panjang_m} onChange={(e) => setForm({ ...form, panjang_m: e.target.value })} data-testid="truck-panjang" />
            <Input type="number" step="0.1" placeholder="Lebar bak" value={form.lebar_m} onChange={(e) => setForm({ ...form, lebar_m: e.target.value })} data-testid="truck-lebar" />
            <Input type="number" step="0.1" placeholder="Tinggi bak" value={form.tinggi_m} onChange={(e) => setForm({ ...form, tinggi_m: e.target.value })} data-testid="truck-tinggi" />
          </div>
          <div className="flex gap-2">
            <Input placeholder="Catatan" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            <Button onClick={submit} data-testid="truck-add-btn"><Plus className="h-4 w-4" /> Tambah</Button>
          </div>
          <p className="text-[11px] text-stone-500">Tipe truk memfilter dropdown di SJ Ready Mix vs SJ Material. Dimensi bak P×L×T untuk auto-prefill SJ Material (mode Kubikasi).</p>
        </div>
      )}
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-stone-50 border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
            <th className="py-3 px-3">Plat</th>
            <th className="py-3 px-3">Tipe</th>
            <th className="py-3 px-3 text-right">Kapasitas</th>
            <th className="py-3 px-3 text-right">P × L × T (m)</th>
            <th className="py-3 px-3">Catatan</th>
            <th className="py-3 px-3 text-right">Aksi</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-stone-400 text-xs">Belum ada truk</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-stone-100">
              <td className="py-2.5 px-3 font-mono font-medium">{r.plate}</td>
              <td className="py-2.5 px-3">{typePill(r.truck_type || "readymix")}</td>
              <td className="py-2.5 px-3 text-right font-mono-num">{formatNumber(r.capacity_m3 || 0)} m³</td>
              <td className="py-2.5 px-3 text-right font-mono-num text-[11px] text-stone-600">
                {(r.panjang_m || r.lebar_m || r.tinggi_m) ? `${formatNumber(r.panjang_m || 0)} × ${formatNumber(r.lebar_m || 0)} × ${formatNumber(r.tinggi_m || 0)}` : "—"}
              </td>
              <td className="py-2.5 px-3 text-stone-500">{r.note || "—"}</td>
              <td className="py-2.5 px-3 text-right">
                {canEdit && <button onClick={() => del(r.id)} className="text-stone-500 hover:text-red-700 px-2"><Trash2 className="h-3.5 w-3.5" /></button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
